import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, num, locString } from "@/lib/format";
import { usePermissions, INVENTORY_STATUS_STYLE } from "@/lib/smartInventory";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, Plus, ScanLine, History, X } from "lucide-react";

const TYPES = [
  ["GERAL", "Inventário geral", "Todas as peças ativas."],
  ["LOCALIZACAO", "Por localização", "Só as peças das localizações escolhidas."],
  ["CATEGORIA", "Por categoria", "Só as peças das categorias escolhidas."],
  ["SELECIONADOS", "Produtos selecionados", "Só as peças que você escolher."],
  ["PARCIAL", "Parcial (o que for contado)", "Sem lista fechada: entra o que a equipe contar."],
];

function CheckList({ items, value, onChange, empty }) {
  if (!items.length) return <p className="text-sm text-slate-500">{empty}</p>;
  return (
    <div className="max-h-48 overflow-y-auto rounded-lg border border-slate-800 p-2 space-y-1">
      {items.map((it) => (
        <label key={it.id} className="flex items-center gap-2 text-sm text-slate-200">
          <input type="checkbox" checked={value.includes(it.id)} onChange={() => onChange(value.includes(it.id) ? value.filter((v) => v !== it.id) : [...value, it.id])} />
          {it.nome || locString(it)}
        </label>
      ))}
    </div>
  );
}

function CreateDialog({ open, onClose, onCreated }) {
  const { user } = useAuth();
  const [form, setForm] = useState({ descricao: "", tipo: "GERAL", localizacao_ids: [], categoria_ids: [], part_ids: [], responsavel_id: "" });
  const [locations, setLocations] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [found, setFound] = useState([]);
  const [chosen, setChosen] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setForm({ descricao: `Inventário ${new Date().toLocaleDateString("pt-BR")}`, tipo: "GERAL", localizacao_ids: [], categoria_ids: [], part_ids: [], responsavel_id: "" });
    setChosen([]); setSearch(""); setFound([]);
    api.get("/locations").then((r) => setLocations(r.data || [])).catch(() => setLocations([]));
    api.get("/categories").then((r) => setCategories(r.data || [])).catch(() => setCategories([]));
    if (user?.role === "ADMINISTRADOR") api.get("/users").then((r) => setUsers((r.data || []).filter((u) => u.active !== false))).catch(() => setUsers([]));
  }, [open, user]);
  useEffect(() => {
    if (form.tipo !== "SELECIONADOS" || search.trim().length < 2) { setFound([]); return; }
    const t = setTimeout(() => api.get("/parts", { params: { q: search, page: 1, page_size: 50 } })
      .then((r) => setFound(r.data.items || [])).catch(() => setFound([])), 300);
    return () => clearTimeout(t);
  }, [search, form.tipo]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async (abrir) => {
    setBusy(true);
    try {
      const body = { ...form, part_ids: chosen.map((p) => p.id), abrir, responsavel_id: form.responsavel_id || undefined };
      const r = await api.post("/inventories/v2", body);
      toast.success(abrir ? "Inventário aberto para contagem." : "Rascunho salvo.");
      onCreated(r.data);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !busy) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">Novo inventário</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label htmlFor="inv-name">Nome</Label><Input id="inv-name" maxLength={120} value={form.descricao} onChange={(e) => set("descricao", e.target.value)} className="bg-slate-950 border-slate-700 mt-1" /></div>
          <div className="space-y-2">
            <Label>Tipo</Label>
            {TYPES.map(([key, label, hint]) => (
              <label key={key} className={`flex gap-3 rounded-lg border p-3 ${form.tipo === key ? "border-sky-600 bg-sky-950/30" : "border-slate-800"}`}>
                <input type="radio" name="inv-type" checked={form.tipo === key} onChange={() => set("tipo", key)} />
                <span><span className="block text-sm font-medium text-slate-100">{label}</span><span className="text-xs text-slate-400">{hint}</span></span>
              </label>
            ))}
          </div>
          {form.tipo === "LOCALIZACAO" && <CheckList items={locations} value={form.localizacao_ids} onChange={(v) => set("localizacao_ids", v)} empty="Cadastre localizações antes." />}
          {form.tipo === "CATEGORIA" && <CheckList items={categories} value={form.categoria_ids} onChange={(v) => set("categoria_ids", v)} empty="Cadastre categorias antes." />}
          {form.tipo === "SELECIONADOS" && <div className="space-y-2">
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar peça por código ou descrição" className="bg-slate-950 border-slate-700" />
            {found.length > 0 && <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-800">
              {found.filter((p) => !chosen.some((c) => c.id === p.id)).map((p) => (
                <button key={p.id} type="button" onClick={() => setChosen((c) => [...c, p])} className="block w-full px-3 py-2 text-left text-sm hover:bg-slate-800">
                  <span className="font-mono text-sky-300">{p.codigo_interno}</span> {p.descricao}
                </button>))}
            </div>}
            <div className="flex flex-wrap gap-1">{chosen.map((p) => (
              <span key={p.id} className="flex items-center gap-1 rounded bg-slate-800 px-2 py-0.5 text-xs">{p.codigo_interno}
                <button type="button" aria-label="Remover" onClick={() => setChosen((c) => c.filter((x) => x.id !== p.id))}><X className="h-3 w-3" /></button></span>))}</div>
          </div>}
          {users.length > 0 && <div><Label>Responsável</Label>
            <Select value={form.responsavel_id || user?.id} onValueChange={(v) => set("responsavel_id", v)}>
              <SelectTrigger className="bg-slate-950 border-slate-700 mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name || u.email}</SelectItem>)}</SelectContent>
            </Select></div>}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" disabled={busy} onClick={() => save(false)}>Salvar rascunho</Button>
          <Button disabled={busy} onClick={() => save(true)} className="bg-sky-700 hover:bg-sky-600">{busy ? "Salvando..." : "Abrir para contagem"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InventoryList() {
  const can = usePermissions();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invs, setInvs] = useState(null);
  const [deleted, setDeleted] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const load = useCallback(() => {
    setInvs(null);
    api.get("/inventories/v2/list", { params: { deleted: deleted || undefined } }).then((r) => setInvs(r.data)).catch((e) => { toast.error(apiErr(e)); setInvs([]); });
  }, [deleted]);
  useEffect(() => { load(); }, [load]);
  const legacy = (invs || []).filter((i) => i.modelo !== 2);
  const current = (invs || []).filter((i) => i.modelo === 2);
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-50">Inventário</h1>
          <p className="text-slate-400 text-sm max-w-2xl">Contagem física por localização, com várias pessoas ao mesmo tempo. O estoque só muda depois da aprovação.</p>
        </div>
        {!deleted && can("inventario.criar") && <Button data-testid="btn-new-inventory" onClick={() => setCreateOpen(true)} className="bg-sky-700 hover:bg-sky-600"><Plus className="h-4 w-4 mr-1" />Novo inventário</Button>}
      </div>
      {user?.role === "ADMINISTRADOR" && <div className="flex gap-2">
        <Button variant={deleted ? "outline" : "default"} onClick={() => setDeleted(false)}>Ativos</Button>
        <Button variant={deleted ? "default" : "outline"} onClick={() => setDeleted(true)}>Excluídos</Button>
      </div>}
      {invs === null ? <Skeleton className="h-64 bg-slate-800" /> : <>
        {current.length === 0 && legacy.length === 0 && <div className="py-16 text-center text-slate-500"><ClipboardList className="mx-auto mb-3 h-12 w-12 opacity-50" />Nenhum inventário criado.</div>}
        <div className="grid gap-3 md:grid-cols-2">
          {current.map((inv) => {
            const r = inv.resumo || {};
            return (
              <Card key={inv.id} className="card-metal p-4 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <Link to={`/inventario/${inv.id}`} className="font-display text-lg font-semibold text-slate-100 hover:underline">{inv.descricao}</Link>
                    <div className="text-xs text-slate-500">{inv.tipo_label} · {fmtDateTime(inv.created_at)} · resp. {inv.responsavel_nome || inv.usuario_nome}</div>
                  </div>
                  <span className={`shrink-0 rounded px-2 py-0.5 text-xs ${INVENTORY_STATUS_STYLE[inv.status] || ""}`}>{inv.status_label}</span>
                </div>
                <div className="flex flex-wrap gap-3 text-xs text-slate-400">
                  <span>{num(r.linhas)} peças contadas</span><span>{num(r.aprovadas)} aprovadas</span>
                  {r.recontagem > 0 && <span className="text-violet-300">{num(r.recontagem)} para recontar</span>}
                  {r.ultima_contagem && <span>última contagem {fmtDateTime(r.ultima_contagem)}</span>}
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={() => navigate(`/inventario/${inv.id}`)} className="bg-slate-700 text-white">Abrir</Button>
                  {["ABERTO", "EM_CONTAGEM", "AGUARDANDO_CONFERENCIA"].includes(inv.status) && can("inventario.contar") &&
                    <Button size="sm" onClick={() => navigate(`/inventario/${inv.id}/contar`)} className="bg-sky-700 text-white"><ScanLine className="h-4 w-4 mr-1" />Contar</Button>}
                </div>
              </Card>
            );
          })}
        </div>
        {legacy.length > 0 && <Card className="card-metal p-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-slate-300"><History className="mr-1 inline h-4 w-4" />{legacy.length} inventário(s) do modelo antigo (contagem por peça, sem localização).</div>
          <Button size="sm" variant="outline" onClick={() => navigate("/inventario/antigos")} className="border-slate-600 bg-slate-900 text-slate-200">Ver inventários antigos</Button>
        </Card>}
      </>}
      <CreateDialog open={createOpen} onClose={() => setCreateOpen(false)} onCreated={(inv) => { setCreateOpen(false); navigate(`/inventario/${inv.id}`); }} />
    </div>
  );
}
